// eslint-disable-next-line react-native/split-platform-components
import { NativeModules, Alert, ToastAndroid } from "react-native";
import { launchCamera } from "react-native-image-picker";
import { uploadImage } from "@services/api";
import { requestCamera } from "@utils/permissions";
import {
  captureProof,
  isNativeCaptureAvailable,
} from "@utils/nativeProofCapture";
import { isValidationActive } from "@utils/proofValidationConfig";

// react-native-image-picker v7 response shape:
//   { didCancel?, errorCode?, errorMessage?, assets?: Asset[] }
//   Asset: { uri, fileName, type, fileSize, width, height, duration, ... }
// Legacy callers here expect a FLAT response with { uri, fileName, type,
// didCancel }. Normalize so neither surface has to change.
const normalize = (r) => {
  if (!r) return { didCancel: true };
  if (r.didCancel) return { didCancel: true };
  if (r.errorCode) return { didCancel: false, error: r.errorMessage };
  const a = r.assets && r.assets[0];
  if (!a) return { didCancel: true };
  return {
    didCancel: false,
    uri: a.uri,
    fileName: a.fileName,
    type: a.type,
    fileSize: a.fileSize,
    width: a.width,
    height: a.height,
    duration: a.duration,
  };
};

const buildFormData = (response) => {
  const data = new FormData();
  data.append("image", {
    name: response.fileName,
    type: response.type,
    uri: response.uri,
  });
  return data;
};

const openCamera = async ({ mediaType, cameraType }) => {
  const r = await launchCamera({
    mediaType,
    quality: 0.4,
    // Bound BOTH axes. With only maxHeight set, the longest edge was
    // unbounded, so the native picker decoded the full-resolution camera
    // bitmap into memory to resize it — a 12–48MP photo becomes tens of MB of
    // ARGB and OOM-crashes on low-RAM (2GB) devices (java.lang.OutOfMemoryError
    // seen in Crashlytics on the delivery-proof capture). Setting both lets
    // BitmapFactory pick an inSampleSize and downscale during decode. 1280px
    // longest edge is ample for delivery/KYC proof legibility.
    maxWidth: 1280,
    maxHeight: 1280,
    cameraType,
    saveToPhotos: false,
    videoQuality: mediaType === "video" ? "medium" : undefined,
    selectionLimit: 1,
  });
  return normalize(r);
};

/**
 * Native delivery-proof capture (live.citymall.partnerdb.proof).
 *
 * Returns the same normalized shape as openCamera plus a `validation` block, so
 * the caller's upload/geotag wiring is identical either way. Returns null when
 * the native path is unusable, which tells the caller to fall back to the
 * system camera rather than fail the capture.
 */
const openNativeCamera = async (validationConfig) => {
  if (!isNativeCaptureAvailable() || !isValidationActive(validationConfig)) {
    return null;
  }
  try {
    const r = await captureProof(validationConfig);
    if (!r || r.didCancel) return { didCancel: true };
    if (!r.uri) return { didCancel: true };
    return {
      didCancel: false,
      uri: r.uri,
      fileName: r.fileName,
      type: r.type || "image/jpeg",
      fileSize: r.fileSize,
      width: r.width,
      height: r.height,
      validation: r.validation ?? null,
    };
  } catch (e) {
    // No foreground activity, camera unavailable, module invalidated. None of
    // these may cost the DB their delivery — hand back to the system camera.
    console.log("[proof-capture] native capture unavailable", e);
    return null;
  }
};

const promptPermissionBlocked = () => {
  const { AppSettingsIntentModule } = NativeModules;
  Alert.alert(
    "Permissions denied",
    "Please grant us Camera access to continue",
    [
      {
        text: "Go to settings",
        onPress: () => AppSettingsIntentModule?.openAppSettings?.(),
      },
      { text: "Cancel", onPress: () => {} },
    ],
    { cancelable: true },
  );
};

/**
 * Shared image / video picker.
 *
 * Back-compat default: source="both" shows the camera/gallery Alert and
 * uploads the picked asset via /media/upload-image (uploadImage). All
 * existing callers (KYCSetup, SupportTree, …) hit this path.
 *
 * Onboarding callers pass `source: "gallery" | "camera"` to skip the
 * Alert and `skipUpload: true` to divert the upload through
 * /cpdb/upload-private-onboarding-image (handled by the caller).
 *
 * Props:
 *   source?: "both" | "camera" | "gallery"        default "both"
 *   mediaType?: "photo" | "video"                 default "photo"
 *   cameraType?: "front" | "back"                 default "back"
 *   isCancellable?: boolean                       default true
 *   uploadFn?: (formData) => Promise<{ data }>    default uploadImage
 *   skipUpload?: boolean                          default false — when true
 *       the helper does NOT upload and hands the normalized response
 *       (uri, fileName, type, ...) to onSuccessCallback.
 *   onSelectCallback?: (response) => void         fires with normalized picker response
 *   onSuccessCallback?: (result) => void          fires with upload result
 *       (or normalized response if skipUpload)
 *   onDismiss?: () => void                        used by the Alert wrapper
 *   onErrorCallback?: (error) => void             fires if the upload throws
 *   validationConfig?: ResolvedValidationConfig   when present AND mode !== "OFF",
 *       source "camera" routes through the native ProofCaptureActivity, which
 *       validates the photo before returning it and adds `validation` to the
 *       result handed to onSuccessCallback. Falls back to the system camera
 *       whenever the native module is missing or fails. See
 *       @utils/proofValidationConfig.
 */
export default function imagePickerHelper(props) {
  const {
    onSelectCallback = () => {},
    onSuccessCallback = () => {},
    onErrorCallback = () => {},
    mediaType = "photo",
    cameraType = "back",
    uploadFn = uploadImage,
    skipUpload = false,
    validationConfig = null,
  } = props;

  const handlePicked = async (response) => {
    onSelectCallback(response);
    try {
      if (!response || response.didCancel) return;
      if (!response.uri) return;

      if (skipUpload) {
        onSuccessCallback(response);
        return;
      }

      const { data: uploadData } = await uploadFn(buildFormData(response));
      // Carry the capture verdict alongside the upload result so the caller can
      // attach it to the image without holding on to the picker response.
      onSuccessCallback(
        response.validation
          ? { ...uploadData, validation: response.validation }
          : uploadData,
      );
    } catch (e) {
      console.log(e);
      ToastAndroid.showWithGravity(
        "Error with uploading image",
        ToastAndroid.SHORT,
        ToastAndroid.CENTER,
      );
      onErrorCallback(e);
    }
  };

  const launchCameraWithPermission = async () => {
    const { granted, statuses } = await requestCamera();
    if (granted) {
      // The native screen validates before returning; a null result means it
      // could not run and the system camera takes over unvalidated.
      const native = await openNativeCamera(validationConfig);
      if (native) return handlePicked(native);
      const r = await openCamera({ mediaType, cameraType });
      return handlePicked(r);
    }
    const isBlocked = Object.values(statuses || {}).some(
      (s) => s === "blocked",
    );
    if (isBlocked) promptPermissionBlocked();
    return undefined;
  };

  // App-wide policy: gallery uploads are disabled outside the KYC / Onboarding
  // document steps (which use their own pickers). Every caller of this helper is
  // camera-only; the `source` prop (if passed) is ignored.
  launchCameraWithPermission();
}
